import React, { useState, useEffect } from 'react';
import {
  Alert,
  Box,
  IconButton,
  Tooltip,
  Paper,
  Stack,
  Button,
  Avatar,
  Chip,
  CircularProgress,
} from '@mui/material';
import axios from 'axios';
import dayjs from 'dayjs';
import AddTeamDrawer from './AddTeamDrawer';
import { align_center, align_left, dataGridStyle, vmAddButtonSx, vmActionButtonSx, vmPageTitleSx } from '../../Styles/VMStyles';
import { API_DELETE_TEAM, API_GET_ROLE_NAMES, API_TEAMS_INFO } from '../../Util/URLConstants';
import StorageFactory from '../../Util/StorageFactory';
import {
  ROUTE_ROLES,
  ROUTE_SUBSCRIPTION,
  SESSION_USER_ID_KEY,
  SESSION_USER_TYPE_KEY,
} from '../../Util/VMConstants';
import MUITypography from '../../Components/MUITypography';
import EditNoteIcon from '@mui/icons-material/EditNote';
import DeleteIcon from '@mui/icons-material/Delete';
import { isAdminLoggedIn, renderSNo } from '../../Util/UtilHelper';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import ReusableDataGrid from '../../Components/ReusableDataGrid';
import SubscriptionGateDialog from '../../Util/SubscriptionGateDialog';
import {
  formatTeamUsageLabel,
  formatTrialTeamNotice,
  isTeamAddAllowed,
  isTrialSubscriptionPlan,
  SUBSCRIPTION_ACTION_SELECT_PLAN,
} from '../../Util/subscriptionLifecycleHelper';
import {
  formatExpiresOnWithRemaining,
  formatSubscriptionExpiryDate,
  formatSubscriptionRemainingLabel,
  getTrialOrSubscriptionExpiryDate,
} from '../../Util/subscriptionTierHelper';
import { fetchTeamUserLimitTier } from '../../Redux/slices/subscriptionTierSlice';

function VMTeam() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [rowCount, setRowCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [gateChecking, setGateChecking] = useState(false);
  const search = '';
  const startDate = null;
  const endDate = null;
  const [rows, setRows] = useState([]);
  const [gateDialog, setGateDialog] = useState({
    open: false,
    message: '',
    action: null,
    plan: null,
    plan_tier: null,
    max_users: null,
  });
  const [, setRoleOptions] = useState([]);

  const teamLimit = useSelector((state) => state.subscriptionTier.teamLimit);
  const subscriptionStatus = useSelector(
    (state) => state.subscriptionTier.status
  );
  const sessionFactory = new StorageFactory('session');
  const userID = sessionFactory.get(SESSION_USER_ID_KEY);
  const userType = sessionFactory.get(SESSION_USER_TYPE_KEY);
  const isAdmin = userType?.toLowerCase() === 'admin';

  const openGateDialog = (gate) => {
    setGateDialog({
      open: true,
      message: gate?.message || '',
      action:
        gate?.action ||
        (isTrialSubscriptionPlan(gate) ? SUBSCRIPTION_ACTION_SELECT_PLAN : 'upgrade'),
      plan: gate?.plan ?? null,
      plan_tier: gate?.plan_tier ?? null,
      max_users:
        gate?.max_users ?? gate?.usage?.max_users ?? null,
    });
  };

  const closeGateDialog = () => {
    setGateDialog({
      open: false,
      message: '',
      action: null,
      plan: null,
      plan_tier: null,
      max_users: null,
    });
  };

  useEffect(() => {
    if (userID && isAdmin) {
      dispatch(fetchTeamUserLimitTier(userID));
    }
  }, [dispatch, userID, isAdmin]);

 
  const fetchTeamsById = async () => {
    try {
      setLoading(true);
      const res = await axios.get(API_TEAMS_INFO, {
        params: {
          user_id: userID,
          limit: pageSize,
          offset: page * pageSize,
          search,
          start_date: startDate ? dayjs(startDate).format('YYYY-MM-DD') : undefined,
          end_date: endDate ? dayjs(endDate).format('YYYY-MM-DD') : undefined,
        },
      });

      if (res.data?.status_code === 200) {
        const formattedData = (res.data?.data || []).map((item, idx) => ({
          ...item,
          s_no: idx + 1 + page * pageSize,
        }));
        setRows(formattedData);
        setRowCount(res.data.pagination?.totalCount || 0);
      } else {
        setRows([]);
        setRowCount(0);
      }
    } catch (err) {
      console.error('Error fetching teams:', err);
      setRows([]);
      setRowCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTeamMember = async (user_id) => {
    const confirmed = window.confirm('Are you sure you want to delete this team member?');
    if (!confirmed) return;

    try {
      setLoading(true);
      const res = await axios.delete(`${API_DELETE_TEAM}/${user_id}`);

      if (res.data?.status_code === 200) {
        fetchTeamsById();
        if (userID && isAdmin) {
          dispatch(fetchTeamUserLimitTier(userID));
        }
      } else {
        alert(res.data?.message || 'Failed to delete.');
      }
    } catch (err) {
      console.error('Delete Error:', err);
      alert('Something went wrong while deleting.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!drawerOpen) {
      fetchTeamsById(); // refresh after drawer closes
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawerOpen]);

  useEffect(() => {
    fetchTeamsById();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, search, startDate, endDate]);

  const columns = [
        renderSNo(),
    {
      field: 'user_id',
      headerName: 'User ID',
      flex: 0.7,
      sortable: false,
      renderCell: (params) => (
        <Box
          sx={{
            width: '100%',
            whiteSpace: 'normal',
            wordBreak: 'break-word',
            overflowWrap: 'anywhere',
          }}
        >
          <MUITypography variant="body2">{params.value}</MUITypography>
        </Box>
      ),
    },
    {
      field: 'name',
      headerName: 'Name',
      flex: 0.6,
      sortable: false,
      // renderCell: (params) => <MUITypography variant="body2">{params.value}</MUITypography>,
    },
    {
      field: 'phone_combined',
      headerName: 'Phone Number',
      flex: 0.7,
      sortable: false,
      renderCell: (params) => {
        const { country_code, mobile_no } = params.row;
        return (
          <Box sx={{...align_left,height:'100%',whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
            <MUITypography variant="body2">
            {country_code ? `+${country_code}` : ''} {mobile_no || ''}
          </MUITypography>
          </Box>
        );
      },
    },
    {
      field: 'email',
      headerName: 'Email',
      flex: 1,
      sortable: false,
      renderCell: (params) => (
        <Box
          sx={{
            width: '100%',
            whiteSpace: 'normal',
            wordBreak: 'break-word',
            overflowWrap: 'anywhere',
          }}
        >
          <MUITypography variant="body2">{params.value}</MUITypography>
        </Box>
      ),
    },
    {
      field: 'user_type',
      headerName: 'Role',
      flex: 0.5,
      sortable: false,
      
      renderCell: (params) => (
        <Box
          sx={{
            width: '100%',
            whiteSpace: 'normal',
            wordBreak: 'break-word',
            overflowWrap: 'anywhere',
          }}
        >
          <MUITypography variant="body2">{params.value}</MUITypography>
        </Box>
      ),
    },
    {
      field: 'expireOn',
      headerName: 'Expires On',
      flex: 1.1,
      sortable: false,
      renderCell: (params) => {
        const expireOn =
          params.row.expireOn ||
          params.row.expire_on ||
          params.row.subscription_end_date ||
          params.row.license_expires_at ||
          null;
        const dateLabel = expireOn ? formatSubscriptionExpiryDate(expireOn) : '';
        const remaining = expireOn ? formatSubscriptionRemainingLabel(expireOn) : '';
        const label =
          dateLabel && remaining
            ? `${dateLabel} (${remaining})`
            : dateLabel || '';
        return (
          <Box
            sx={{
              width: '100%',
              whiteSpace: 'normal',
              wordBreak: 'break-word',
              overflowWrap: 'anywhere',
            }}
          >
            <MUITypography variant="body2">
              {label || '—'}
            </MUITypography>
          </Box>
        );
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 0.6,
      sortable: false,
      renderCell: (params) => {
        const { is_otp_verified ,user_type} = params.row;
        if (is_otp_verified) {
          return (
           <Box sx={align_center}>
            {
              !isAdminLoggedIn(user_type) 
              ?              <Stack direction="row" spacing={1}>
              <Tooltip title="Edit">
                <IconButton
                  onClick={() => {
                    setSelectedUser(params.row);
                    setDrawerOpen(true);
                  }}
                  color="primary"
                  size="small"
                >
                  <EditNoteIcon />
                </IconButton>
              </Tooltip>

              <Tooltip title="Delete">
                <IconButton
                  onClick={() => handleDeleteTeamMember(params.row.user_id)}
                  color="error"
                  size="small"
                >
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            </Stack>
              :'---'
            }

           </Box>
          );
        }

        return (
          <Stack direction="row" spacing={1} alignItems="center">
            <Stack direction="column" spacing={0.5} sx={align_center}>
              <MUITypography variant="caption" color="error">
                OTP not verified
              </MUITypography>
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  setSelectedUser(params.row);
                  setDrawerOpen(true);
                }}
              >
                Verify
              </Button>
            </Stack>

            <Tooltip title="Delete">
              <IconButton
                onClick={() => handleDeleteTeamMember(params.row.user_id)}
                color="error"
                size="small"
              >
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        );
      },
    },
  ];

  const handleAddClick = async () => {
    setGateChecking(true);
    try {
      const limit = await dispatch(fetchTeamUserLimitTier(userID)).unwrap();

      if (!isTeamAddAllowed(limit)) {
        openGateDialog({
          message: limit.message,
          action:
            limit.action ||
            (isTrialSubscriptionPlan(limit)
              ? SUBSCRIPTION_ACTION_SELECT_PLAN
              : 'upgrade'),
          plan: limit.plan,
          plan_tier: limit.plan_tier,
          max_users: limit.max_users ?? limit.usage?.max_users,
        });
        return;
      }

      const roles = await fetchRoles();

      if (!roles || roles.length === 0) {
        alert(
          'No roles found. Create a new role to manage permissions and access for your team members.'
        );
        navigate(`/${ROUTE_ROLES}`);
        return;
      }

      setSelectedUser(null);
      setDrawerOpen(true);
    } catch (err) {
      console.error('checkTeamUserLimit error:', err);
      alert('Unable to verify team limit. Please try again.');
    } finally {
      setGateChecking(false);
    }
  };



  const usageLabel = formatTeamUsageLabel(teamLimit);
  const isTrialPlan = isTrialSubscriptionPlan(teamLimit);
  const canAddTeamMember = !teamLimit || isTeamAddAllowed(teamLimit);
  const trialNotice = isTrialPlan ? formatTrialTeamNotice(teamLimit) : '';
  const trialExpiryLabel = isTrialPlan
    ? formatExpiresOnWithRemaining(
        getTrialOrSubscriptionExpiryDate(subscriptionStatus, teamLimit)
      )
    : '';

  const fetchRoles = async () => {
  try {
    const res = await axios.get(API_GET_ROLE_NAMES + "?user_id=" + userID);
    const resData = res.data;

    if (resData.status_code === 200 && Array.isArray(resData.data)) {
      setRoleOptions(resData.data);
      return resData.data; // ✅ IMPORTANT
    }
    return [];
  } catch (err) {
    console.error("Error fetching roles:", err);
    return [];
  }
};

  return (
    <Box sx={{ px: 0, p:2 ,mr:1}}> 
<Box
      display="flex"
      alignItems="center"
      // px={2}
      py={1}
      sx={{ width: '100%',mb:2 }}
    >
      <MUITypography variant="pageTitle" sx={vmPageTitleSx}>
      My Team
      </MUITypography>
      <Box sx={{ ml: 'auto', mr: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        {isTrialPlan ? (
          <Chip
            label={
              trialExpiryLabel
                ? `Trial plan · ${trialExpiryLabel}`
                : 'Trial plan'
            }
            size="small"
            color="warning"
            variant="outlined"
          />
        ) : null}
        {usageLabel ? <Chip label={usageLabel} size="small" variant="outlined" /> : null}
        {isAdmin && (
          <Button
            variant="contained"
            disableElevation
            sx={{
              ...vmActionButtonSx,
              backgroundColor: '#FEB449',
              color: '#000',
              border: 'none',
              borderRadius: '50px',
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': {
                backgroundColor: '#F5A832',
              },
            }}
            onClick={() => navigate(`/${ROUTE_SUBSCRIPTION}`)}
          >
            Subscribe Now
          </Button>
        )}
        <Tooltip
          title={
            !canAddTeamMember && isTrialPlan
              ? 'Trial team limit reached. Subscribe to add more users.'
              : !canAddTeamMember
                ? 'Team member limit reached. Upgrade your plan to add more users.'
                : ''
          }
        >
          <span>
            <Button
              variant="contained"
              disabled={gateChecking || !canAddTeamMember}
              startIcon={
                <Avatar
                  alt="Add"
                  src="/static/add_circle.svg"
                  sx={{
                    backgroundColor: 'transparent',
                    boxShadow: 'none',
                    width: 24,
                    height: 24,
                    cursor: 'pointer',
                  }}
                />
              }
              onClick={handleAddClick}
              sx={vmAddButtonSx}
            >
              Add Team
            </Button>
          </span>
        </Tooltip>
        {gateChecking ? (
          <CircularProgress size={22} sx={{ color: 'primary.main' }} />
        ) : null}
      </Box>
      
    </Box>

      {isTrialPlan && trialNotice ? (
        <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
          {trialNotice}
        </Alert>
      ) : null}

      <Paper
        elevation={3}
        sx={{
          mt: 2,
          mb: 4,
          borderRadius: 2,
          overflow: 'hidden',
          width: '100%',
          p: 0,
        }}
      >
        <ReusableDataGrid
          rows={rows}
          columns={columns}
          rowCount={rowCount}
          pagination
          paginationMode="server"
          paginationModel={{ page, pageSize }}
          onPaginationModelChange={({ page, pageSize }) => {
            setPage(page);
            setPageSize(pageSize);
          }}
          pageSizeOptions={[5, 10, 20, 50]}
          loading={loading || gateChecking}
          getRowHeight={() => 'auto'} 
          disableRowSelectionOnClick
          disableColumnFilter
          disableColumnSelector
          disableDensitySelector
          disableColumnMenu
          hideFooterSelectedRowCount
          autoHeight
          sx={dataGridStyle}
        />
      </Paper>

      <AddTeamDrawer
        open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setSelectedUser(null);
          if (userID && isAdmin) {
            dispatch(fetchTeamUserLimitTier(userID));
          }
        }}
        selectedUser={selectedUser}
        isEdit={!!selectedUser}
        onSubscriptionGate={(gate) => {
          setDrawerOpen(false);
          setSelectedUser(null);
          openGateDialog(gate);
        }}
      />

      <SubscriptionGateDialog
        open={gateDialog.open}
        onClose={closeGateDialog}
        message={gateDialog.message}
        action={gateDialog.action}
        plan={gateDialog.plan}
        plan_tier={gateDialog.plan_tier}
        max_users={gateDialog.max_users}
        onConfirm={() => {
          closeGateDialog();
          navigate(`/${ROUTE_SUBSCRIPTION}`);
        }}
      />
    </Box>
  );
}

export default VMTeam;
